import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../../utils/formatUtils';
import { formatDate } from '../../../../utils/date';
import { 
  Eye, Download, Filter, Search, ChevronDown, CalendarRange, 
  Plus, Edit, Trash2, ChevronLeft, AlertCircle, CheckCircle, Clock 
} from 'lucide-react';
import Message from '../../../../components/Message';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { getBills, deleteBill } from '../../../../services/finance_services/billService';
import { exportBillsListExcel } from '../../../../services/export_services/billExportService';
import Pagination from '../../../../components/Pagination';
import useApi from '../../../../hooks/useApi';

const InviterBillsPage = () => {
  const { invitationId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  
  const { data, loading, error: apiError, callApi } = useApi(getBills, {showSuccessToast: false});
  const { loading: deleting, callApi: deleteBillApi } = useApi(deleteBill, { 
    showSuccessToast: true, 
    msg: 'Bill deleted successfully' 
  });
  
  const [bills, setBills] = useState([]);
  const [pagination, setPagination] = useState({});
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [permissionLevel, setPermissionLevel] = useState(null);
  const [inviter, setInviter] = useState(state?.inviter || null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    dateRange: '',
    startDate: '',
    endDate: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  // Remove modal states that are no longer needed
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBillId, setDeleteBillId] = useState(null);
  
  // Download/export states
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchBills();
  }, [invitationId, currentPage, pageSize, searchTerm, filters]);

  useEffect(() => {
    if (data) {
      setBills(data.results || []);
      setPagination({
        current_page: data.current_page,
        total_pages: data.total_pages,
        total_count: data.count
      });
      setAvailableStatuses(['pending', 'paid', 'overdue', 'partially_paid', 'cancelled']);
      setPermissionLevel(data.permission_level); // Set based on invitation context
      setInviter(state?.inviter || null);
    }
  }, [data, state]);

  const fetchBills = async () => {
    const billFilters = {
      search: searchTerm,
      status: filters.status,
      date_range: filters.dateRange,
      start_date: filters.startDate,
      end_date: filters.endDate,
    };
    
    await callApi(currentPage, pageSize, billFilters, invitationId);
  };

  const canManage = permissionLevel === 'full_management';
  const canDownload = permissionLevel === 'view_download' || canManage;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      dateRange: '',
      startDate: '',
      endDate: '',
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleDownload = async (billId) => {
    setDownloadingId(billId);
    setDownloadMessage('');
    setDownloadError('');
    
    try {
      // Use the attachment service for downloading
      const { downloadBillAttachment } = await import('../../../../services/finance_attachment_services/billAttachmentService');
      await downloadBillAttachment(billId, invitationId);
      setDownloadMessage('Bill PDF downloaded successfully!');
    } catch (err) {
      setDownloadError(err.message || 'Download failed');
    } finally {
      setDownloadingId(null);
      setTimeout(() => {
        setDownloadMessage('');
        setDownloadError('');
      }, 3000);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setDownloadMessage('');
    setDownloadError('');
    
    try {
      const exportFilters = {
        start_date: filters.startDate,
        end_date: filters.endDate,
      };
      
      const result = await exportBillsListExcel(exportFilters, invitationId);
      if (result.success) {
        setDownloadMessage('Bills exported to Excel successfully!');
      } else {
        setDownloadError(result.error || 'Export failed');
      }
    } catch (err) {
      setDownloadError(err.message || 'Export failed');
    } finally {
      setExporting(false);
      setTimeout(() => {
        setDownloadMessage('');
        setDownloadError('');
      }, 3000);
    }
  };

  const handleViewBill = (billId) => {
    navigate(`/dashboard/collaborator/${invitationId}/bills/${billId}/details`, {
      state: { inviter, permissionLevel }
    });
  };

  const handleEditBill = (bill) => {
    navigate(`/dashboard/collaborator/${invitationId}/bills/${bill.id}/edit`, {
      state: { inviter, permissionLevel, reportData: bill }
    });
  };

  const handleDeleteBill = (billId) => {
    setDeleteBillId(billId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteBillApi(deleteBillId, invitationId);
      setShowDeleteModal(false);
      fetchBills();
    } catch (err) {
      console.error('Error deleting bill:', err);
      setShowDeleteModal(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleBackToSummary = (invId) => {
    navigate(`/dashboard/collaborator/${invId}`,);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'green', icon: CheckCircle, label: 'Paid' },
      pending: { color: 'yellow', icon: Clock, label: 'Pending' },
      overdue: { color: 'red', icon: AlertCircle, label: 'Overdue' },
      partially_paid: { color: 'blue', icon: Clock, label: 'Partially Paid' },
      cancelled: { color: 'gray', icon: AlertCircle, label: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Remove full-page modal views since we're using proper routing now
  return (
    <>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this bill?"
      />

      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <button 
              onClick={() => handleBackToSummary(invitationId)}
              className="mb-2 text-primary-600 hover:text-primary-800 flex items-center text-sm"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to Summary
            </button>
            <h1 className="text-3xl font-bold text-gray-800">
              Bills - {inviter?.username}
            </h1>
            <p className="text-gray-600 mt-1">
              Total: {pagination.total_count || 0} bills
            </p>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input 
                type="text" 
                placeholder="Search bills..." 
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            
            {/* Actions */}
            <div className="flex gap-2">
              <button 
                onClick={() => setShowFilters(!showFilters)} 
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm flex items-center hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2 text-gray-500" />
                Filters
                <ChevronDown className={`h-4 w-4 ml-2 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              <button 
                onClick={resetFilters} 
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Reset
              </button>
              {canDownload && (
                <button 
                  onClick={handleExport}
                  disabled={exporting}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm flex items-center text-gray-700 hover:bg-gray-50"
                >
                  <Download className="h-4 w-4 mr-2 text-gray-500" />
                  {exporting ? 'Exporting...' : 'Export'}
                </button>
              )}
            </div>
          </div>
          
          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  name="status"
                  className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  {availableStatuses.map(status => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarRange className="h-4 w-4 text-gray-400" />
                  </div>
                  <select
                    name="dateRange"
                    className="w-full pl-10 border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={filters.dateRange}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
              </div>
              
              {filters.dateRange === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={filters.startDate}
                      onChange={handleFilterChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={filters.endDate}
                      onChange={handleFilterChange}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Messages */}
        {downloadMessage && <Message type="success" message={downloadMessage} />}
        {downloadError && <Message type="error" message={downloadError} />}
        {apiError && <Message type="error" message={apiError} />}

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          {canManage && (
            <button 
              onClick={() => navigate(`/dashboard/collaborator/${invitationId}/bills/add`, {
                state: { inviter, permissionLevel }
              })}
              className="flex items-center px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Bill
            </button>
          )}
          <button 
            onClick={fetchBills}
            className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                      Loading bills...
                    </td>
                  </tr>
                ) : bills.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                      No bills found.
                    </td>
                  </tr>
                ) : (
                  bills.map(bill => (
                    <tr key={bill.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {bill.bill_number || `BILL-${bill.id}`}
                        </div>
                        {bill.title && (
                          <div className="text-xs text-gray-500">{bill.title}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{bill.vendor}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(bill.amount)}
                        </div>
                        {bill.amount_paid > 0 && (
                          <div className="text-xs text-gray-500">
                            Paid: {formatCurrency(bill.amount_paid)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(bill.due_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(bill.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {canDownload && (
                            <button
                              onClick={() => handleDownload(bill.id)}
                              className="text-primary-600 hover:text-primary-800"
                              disabled={downloadingId === bill.id}
                            >
                              <Download className="h-5 w-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewBill(bill.id)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          {canManage && (
                            <>
                              <button
                                onClick={() => handleEditBill(bill)}
                                className="text-yellow-600 hover:text-yellow-900"
                              >
                                <Edit className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteBill(bill.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Component */}
          <Pagination
            currentPage={pagination.current_page || 1}
            totalPages={pagination.total_pages || 1}
            totalItems={pagination.total_count || 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
            itemName="bills"
          />
        </div>
      </div>
    </>
  );
};

export default InviterBillsPage;